/**
 * The history's small glyphs, drawn on a 16 px grid in the approved mock's
 * hand (mock.html, 2026-09-24): monochrome, currentColor, never a brand mark.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const PATHS = {
  web: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M1.8 8h12.4M8 1.8c1.7 1.8 2.6 3.9 2.6 6.2S9.7 12.4 8 14.2C6.3 12.4 5.4 10.3 5.4 8S6.3 3.6 8 1.8z" />
    </>
  ),
  mac: (
    <>
      <rect x="2.6" y="3" width="10.8" height="7.6" rx="1.3" />
      <path d="M1.2 13h13.6" />
    </>
  ),
  telegram: (
    <>
      <path d="M14.3 1.7 6.9 9.1" />
      <path d="M14.3 1.7 9.7 14.3 6.9 9.1 1.7 6.3z" />
    </>
  ),
  whatsapp: <path d="M2.4 13.6l.9-2.8a5.9 5.9 0 1 1 2.1 2z" />,
  imessage: (
    <path d="M8 2.3c3.6 0 6.4 2.2 6.4 5s-2.8 5-6.4 5c-.9 0-1.7-.1-2.4-.4l-3.3 1.4.9-2.6C2.2 9.8 1.6 8.7 1.6 7.3c0-2.8 2.8-5 6.4-5z" />
  ),
  slack: <path d="M6.2 2.2v11.6M9.8 2.2v11.6M2.2 6.2h11.6M2.2 9.8h11.6" />,
  voice: <path d="M3.5 6.5v3M6.5 4v8M9.5 5.5v5M12.5 7v2" />,
  open: (
    <>
      <path d="M6.5 3H3.5a1 1 0 0 0-1 1v8.5a1 1 0 0 0 1 1H12a1 1 0 0 0 1-1V9.5" />
      <path d="M9.5 2.5h4v4M13.5 2.5 7.8 8.2" />
    </>
  ),
  alert: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.8v3.7" />
      <circle cx="8" cy="11.1" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  plus: <path d="M8 3.5v9M3.5 8h9" />,
  cross: <path d="M5 5l6 6M11 5l-6 6" />,
  nami: (
    <>
      <circle cx="8" cy="8" r="4.2" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none" />
      <path d="M8 1.8a6.2 6.2 0 0 1 4.6 2M8 14.2a6.2 6.2 0 0 1-4.6-2" />
    </>
  ),
  /** The receipt tile when no toolkit's own icon is known: a neutral action. */
  action: <path d="M9 1.8 3.8 9h4l-1 5.2L12.2 7h-4z" />,
} satisfies Record<string, ReactNode>;

export type GlyphName = keyof typeof PATHS;

export function Glyph({
  name,
  className,
  strokeWidth = 1.4,
}: {
  name: GlyphName;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("size-4 flex-none", className)}
    >
      {PATHS[name]}
    </svg>
  );
}
